/**
 * Unified Markdown Processor
 *
 * Consolidates all markdown formatting logic from the codebase into a single,
 * comprehensive utility. Supports both complete content and streaming (partial content).
 *
 * This replaces scattered implementations:
 * - convertMarkdownToHtml() from agenticWorkflows/helpers.ts
 * - formatResearchToHtml() from agenticWorkflows/helpers.ts
 * - Custom formatting in various tools
 *
 * @module markdownProcessor
 */

import {
  sanitizeHtmlInput,
  trimChatHtmlWhitespace,
  linkToken,
  LINK_TOKEN_RE,
  BARE_URL_RE,
  splitTrailingPunctuation,
  buildExternalLinkAnchor,
  escapeHtmlText,
} from '../components/GlobalChatSystem/utils';

// ============================================================================
// Configuration & Types
// ============================================================================

export interface MarkdownOptions {
  /** Enable HTML sanitization (default: true) */
  sanitize?: boolean;

  /** Auto-linkify bare URLs (default: true) */
  autoLink?: boolean;

  /** Convert citation references [1] to superscript (default: true) */
  formatCitations?: boolean;

  /** Parse and render markdown tables (default: true) */
  parseTables?: boolean;

  /** Enable code block syntax highlighting classes (default: true) */
  codeBlocks?: boolean;

  /** Normalize ALL CAPS headers to Sentence Case (default: false) */
  normalizeHeaders?: boolean;

  /** Source type for special handling (default: 'generic') */
  sourceType?: 'perplexity' | 'claude' | 'generic';
}

export const DEFAULT_MARKDOWN_OPTIONS: MarkdownOptions = {
  sanitize: true,
  autoLink: true,
  formatCitations: true,
  parseTables: true,
  codeBlocks: true,
  normalizeHeaders: false,
  sourceType: 'generic',
};

// ============================================================================
// Main API
// ============================================================================

/**
 * Convert markdown to sanitized HTML
 * Unified entry point for all markdown processing
 *
 * @param markdown - Markdown text to convert
 * @param options - Configuration options
 * @returns Sanitized HTML string
 *
 * @example
 * ```typescript
 * const html = markdownToHtml('**Bold** and *italic*');
 * // Returns: '<strong>Bold</strong> and <em>italic</em>'
 * ```
 */
export function markdownToHtml(
  markdown: string,
  options?: Partial<MarkdownOptions>
): string {
  const opts = { ...DEFAULT_MARKDOWN_OPTIONS, ...options };

  if (!markdown || markdown.trim() === '') return '';

  let html = markdown;

  // Step 1: Parse code blocks (MUST be first to avoid escaping issues).
  // R7: coerce a dangling trailing fence closed, then stash parsed blocks
  // behind placeholders so steps 2-12 can never mangle code content.
  let stashedCodeBlocks: string[] = [];
  if (opts.codeBlocks) {
    html = parseCodeBlocks(coerceUnclosedTrailingFence(html));
    const stashed = stashCodeBlocks(html);
    html = stashed.html;
    stashedCodeBlocks = stashed.blocks;
  }

  // Step 2: Parse markdown tables (MUST be before other parsing)
  if (opts.parseTables) {
    html = parseMarkdownTables(html);
  }

  // Step 3 (link-integrity fix, 2026-08-11): stash inline code spans,
  // markdown links, and bare URLs behind opaque tokens BEFORE the formatting
  // passes — underscores/asterisks/tildes inside a URL can never be eaten as
  // emphasis (the OAuth-URL screenshot defect: parseInlineFormatting used to
  // run before autoLinkUrls and split raw query strings at `_…_` pairs).
  // Restored as finished anchors in step 10.5, BEFORE sanitization, so every
  // href — including a hostile one — still hits the sanitizer.
  const stashedLinks = stashLinksAndCode(html, opts.autoLink !== false);
  html = stashedLinks.html;

  // Step 4: Parse headers with optional normalization
  html = parseHeaders(html, opts.normalizeHeaders);

  // Step 5: Parse bold, italic, strikethrough, inline code
  html = parseInlineFormatting(html);

  // Step 6: Parse lists
  html = parseLists(html);

  // Step 6.5 (2026-08-17): group `> ` quote runs into <blockquote> — email
  // drafts and quoted replies previously rendered as literal "> " prose.
  html = parseBlockquotes(html);

  // Step 7: Parse horizontal rules
  html = parseHorizontalRules(html);

  // Step 8: Format citations
  if (opts.formatCitations) {
    html = formatCitations(html);
  }

  // (Step 9 — bare-URL autolinking — now happens inside step 3's stash.)

  // Step 10: Convert line breaks
  html = convertLineBreaks(html);

  // Step 10.5: restore stashed links/code spans so sanitize still validates
  // every anchor.
  html = restoreLinksAndCode(html, stashedLinks.tokens);

  // Step 11: Sanitize HTML
  if (opts.sanitize) {
    html = sanitizeHtml(html);
  }

  // Step 12: Normalize whitespace
  html = trimHtmlWhitespace(html);

  // Step 13 (R7): restore code blocks untouched by steps 2-12. Their content
  // was escapeHtml'd at parse time, so reinserting after sanitize is safe.
  html = restoreCodeBlocks(html, stashedCodeBlocks);

  return html;
}

/**
 * Convert markdown to HTML optimized for streaming
 * Handles incremental content gracefully (partial markdown)
 *
 * This version is more forgiving of incomplete markdown structures
 * that occur during streaming (unclosed code blocks, partial tables, etc.)
 *
 * @param accumulatedMarkdown - Accumulated markdown so far
 * @param options - Configuration options
 * @returns Sanitized HTML string
 *
 * @example
 * ```typescript
 * // During streaming
 * const html1 = markdownToHtmlStreaming('**Bold'); // Handles unclosed bold
 * const html2 = markdownToHtmlStreaming('**Bold** text'); // Complete
 * ```
 */
export function markdownToHtmlStreaming(
  accumulatedMarkdown: string,
  options?: Partial<MarkdownOptions>
): string {
  const opts = { ...DEFAULT_MARKDOWN_OPTIONS, ...options };

  if (!accumulatedMarkdown || accumulatedMarkdown.trim() === '') return '';

  let html = accumulatedMarkdown;

  // Check for incomplete structures
  const hasUnclosedCodeBlock = (html.match(/```/g) || []).length % 2 !== 0;
  // Unclosed "[text" OR a link whose href is still streaming ("[label](https:…"
  // with no closing paren yet) — the second clause replaces the old
  // autoLinkUrls "(?<!]\()" lookbehind so a partial href is never rendered as
  // a bogus anchor that visibly snaps into place a tick later (review F9).
  const hasIncompleteLink = /\[[^\]]+$|\]\([^)\s]*$/.test(html);

  // Step 1: Code blocks (skip if unclosed). R7: parsed blocks ride behind
  // placeholders so the later steps can't mangle code content (same
  // protection as markdownToHtml).
  let stashedCodeBlocks: string[] = [];
  if (!hasUnclosedCodeBlock && opts.codeBlocks) {
    html = parseCodeBlocks(html);
    const stashed = stashCodeBlocks(html);
    html = stashed.html;
    stashedCodeBlocks = stashed.blocks;
  }

  // Step 2: Tables (use partial parser for streaming)
  if (opts.parseTables) {
    html = parseMarkdownTablesPartial(html);
  }

  // Step 3 (link-integrity fix, 2026-08-11): stash code spans, markdown
  // links, and bare URLs behind opaque tokens (bare-URL autolinking skipped
  // while a markdown link is still incomplete — the next streaming tick
  // reprocesses the accumulated source from scratch, so this self-heals).
  const stashedLinks = stashLinksAndCode(html, opts.autoLink !== false && !hasIncompleteLink);
  html = stashedLinks.html;

  // Step 4: Headers
  html = parseHeaders(html, opts.normalizeHeaders);

  // Step 5: Inline formatting
  html = parseInlineFormatting(html);

  // Step 6: Lists
  html = parseLists(html);

  // Step 6.5: blockquote runs (same as markdownToHtml — the grouping is
  // line-complete per tick and self-heals as the stream accumulates).
  html = parseBlockquotes(html);

  // Step 7: Horizontal rules
  html = parseHorizontalRules(html);

  // Step 8: Citations
  if (opts.formatCitations) {
    html = formatCitations(html);
  }

  // (Step 9 — bare-URL autolinking — now happens inside step 3's stash.)

  // Step 10: Line breaks
  html = convertLineBreaks(html);

  // Step 10.5: restore stashed links/code spans BEFORE sanitize.
  html = restoreLinksAndCode(html, stashedLinks.tokens);

  // Step 11: Sanitize
  if (opts.sanitize) {
    html = sanitizeHtml(html);
  }

  // Step 12: Trim whitespace
  html = trimHtmlWhitespace(html);

  // Step 13 (R7): restore code blocks untouched by steps 2-12.
  html = restoreCodeBlocks(html, stashedCodeBlocks);

  return html;
}

// ============================================================================
// Parsing Functions (Consolidated from existing code)
// ============================================================================

/**
 * Parse code blocks with language hints
 * Supports: ```language\ncode\n```
 */
function parseCodeBlocks(text: string): string {
  return text.replace(/```(\w+)?\n([\s\S]*?)```/g, (_, lang, code) => {
    const escapedCode = escapeHtml(code.trim());
    const language = lang || 'text';
    return `<pre><code class="language-${language}">${escapedCode}</code></pre>`;
  });
}

// ── R7 code-block protection (2026-08-04, trial-onboarding screenshot) ──────
// A reply carrying a whole file in a fence was rendered as soup: the fence
// was UNCLOSED (cut off / model omission) so it never matched, and even
// matched blocks were re-processed by the later steps (URLs autolinked
// inside code, every newline turned into a literal <br/>). Two rules fix
// the class: (1) a trailing unclosed fence is coerced closed before parsing
// — code to EOF beats rendering code as prose; (2) parsed <pre> blocks are
// swapped for opaque placeholders for the REST of the pipeline and restored
// at the very end, so no later step can touch code content.

/** Close a dangling trailing fence (odd fence count) before parsing. */
function coerceUnclosedTrailingFence(text: string): string {
  const fenceCount = (text.match(/```/g) || []).length;
  if (fenceCount % 2 === 0) return text;
  return `${text}\n\`\`\``;
}

const CODE_BLOCK_PLACEHOLDER = (i: number) => `%%PRESTON-CODEBLOCK-${i}%%`;
const CODE_BLOCK_PLACEHOLDER_RE = /(?:<p>)?%%PRESTON-CODEBLOCK-(\d+)%%(?:<\/p>)?/g;

/** Replace parsed <pre> blocks with placeholders; returns them for restore. */
function stashCodeBlocks(html: string): { html: string; blocks: string[] } {
  const blocks: string[] = [];
  const out = html.replace(/<pre><code[\s\S]*?<\/code><\/pre>/g, (block) => {
    blocks.push(block);
    return CODE_BLOCK_PLACEHOLDER(blocks.length - 1);
  });
  return { html: out, blocks };
}

/** Restore stashed <pre> blocks after every other step (incl. sanitize). */
function restoreCodeBlocks(html: string, blocks: string[]): string {
  if (blocks.length === 0) return html;
  return html.replace(CODE_BLOCK_PLACEHOLDER_RE, (_, i) => blocks[Number(i)] ?? '');
}

/**
 * Parse markdown tables with header/body detection
 * Based on formatResearchToHtml implementation
 */
function parseMarkdownTables(text: string): string {
  const lines = text.split('\n');
  let inTable = false;
  let tableHtml = '';
  let result = '';
  let hasBody = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Detect table row (contains pipes with at least 3 parts)
    if (line.includes('|') && line.split('|').length >= 3) {
      if (!inTable) {
        inTable = true;
        hasBody = false;
        tableHtml = '<table class="markdown-table"><thead><tr>';
      }

      const cells = line
        .split('|')
        .slice(1, -1) // Remove empty first/last
        .map(cell => cell.trim());

      // Check if separator row (| --- | :---: | ---: |).
      //
      // B2 turn 10 part B (2026-09-02, live prod): the pattern here was
      // /^-+:?:?$/ — dashes FIRST — so it matched `---` and `---:` but not
      // `:---` or `:---:`, the left- and centre-aligned forms a model writes
      // for a comparison table. GFM puts the colon on whichever side it
      // aligns to, either side or both.
      //
      // A rejected separator does not merely lose the alignment: it is taken
      // for content, `hasBody` never flips, and every row after it is
      // appended as more <th> into the one open header row. The reported
      // table was a 3-column, 4-row comparison that arrived as a single row
      // of twelve headers, overlapping and overflowing its container.
      //
      // Same rule as the sibling parser's PIPE_TABLE_SEPARATOR_RE
      // (GlobalChatSystem/utils.ts, which got this right in its 2026-08-03
      // review) so the two lanes accept exactly the same separators — and
      // `cells.length > 0` because [].every() is true and an empty row is
      // not a separator.
      const isSeparator = cells.length > 0 && cells.every(c => /^:?-+:?$/.test(c));

      if (isSeparator) {
        tableHtml += '</tr></thead><tbody>';
        hasBody = true;
      } else if (hasBody) {
        // Body row
        tableHtml += '<tr>' + cells.map(c => `<td>${c}</td>`).join('') + '</tr>';
      } else {
        // Header row
        tableHtml += cells.map(c => `<th>${c}</th>`).join('');
      }
    } else {
      // Not a table row
      if (inTable) {
        if (!hasBody) {
          tableHtml += '</tr></thead>';
        }
        tableHtml += '</tbody></table>';
        result += tableHtml + '\n';
        tableHtml = '';
        inTable = false;
      }
      result += line + '\n';
    }
  }

  // Close table if still open
  if (inTable) {
    if (!hasBody) {
      tableHtml += '</tr></thead>';
    }
    tableHtml += '</tbody></table>';
    result += tableHtml;
  }

  return result;
}

/**
 * Parse markdown tables (partial version for streaming)
 * More forgiving of incomplete tables
 */
function parseMarkdownTablesPartial(text: string): string {
  // For streaming, we use the same logic but don't enforce complete table structure
  // This allows partial tables to render progressively
  return parseMarkdownTables(text);
}

// ── URL-safe link tokenization (link-integrity fix, 2026-08-11) ─────────────
// Product-owner screenshots: a Google OAuth URL rendered with the anchor
// covering only ".../oauth2/auth?access" — parseInlineFormatting ran BEFORE
// autoLinkUrls, so the underscore pairs inside the raw query string became
// <em> tags and split the URL before the autolinker ever saw it. Links and
// code spans are now stashed behind opaque placeholder tokens (private-use
// sentinels around "LNK<n>" — see linkToken in GlobalChatSystem/utils) before
// ANY inline-formatting pass, and restored as finished anchors right before
// sanitization, so hostile hrefs still hit the sanitizer.

/**
 * Stash (i) inline code spans, (ii) markdown [text](url) links, and
 * (iii) bare URLs (https?:// and www., gated on `autoLink`) behind opaque
 * tokens. Returns the tokenized text plus the finished HTML fragments.
 */
function stashLinksAndCode(text: string, autoLink: boolean): { html: string; tokens: string[] } {
  const tokens: string[] = [];
  const stash = (fragment: string): string => {
    tokens.push(fragment);
    return linkToken(tokens.length - 1);
  };

  // (i) Inline code spans — opaque: content is never formatted or autolinked.
  //     (Same span shape the old parseInlineFormatting rule matched.)
  let out = text.replace(/`([^`]+)`/g, (_match, code: string) => stash(`<code>${code}</code>`));

  // (ii) Markdown links [text](url): target/rel ride on the anchor itself;
  //      the label still receives inline formatting; the href is opaque so
  //      underscores in it can never become <em>. The sanitizer downstream
  //      re-validates the href (javascript:/data:/… still unwrap).
  out = out.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, label: string, href: string) =>
    stash(
      `<a href="${escapeHtmlText(href)}" target="_blank" rel="noopener noreferrer">${parseInlineFormatting(label)}</a>`
    ));

  // (iii) Bare URLs: trailing sentence punctuation and unbalanced )]} stay
  //       OUTSIDE the anchor (balanced wiki-style parens stay inside).
  if (autoLink) {
    out = out.replace(BARE_URL_RE, (match) => {
      const { url, trailing } = splitTrailingPunctuation(match);
      if (!url || !/[a-z0-9]/i.test(url.replace(/^(?:https?:\/\/|www\.)/i, ''))) return match;
      return stash(buildExternalLinkAnchor(url)) + trailing;
    });
  }

  return { html: out, tokens };
}

/** Restore fragments stashed by stashLinksAndCode (run BEFORE sanitize). */
function restoreLinksAndCode(html: string, tokens: string[]): string {
  if (tokens.length === 0) return html;
  // Looped (bounded) because a stashed fragment can itself embed a token —
  // e.g. a markdown link whose label contained a code span stashed earlier —
  // and String.replace never rescans replacement text (review F7).
  let out = html;
  for (let pass = 0; pass < 5 && LINK_TOKEN_RE.test(out); pass += 1) {
    LINK_TOKEN_RE.lastIndex = 0;
    out = out.replace(LINK_TOKEN_RE, (_match, i: string) => tokens[Number(i)] ?? '');
  }
  LINK_TOKEN_RE.lastIndex = 0;
  return out;
}

/**
 * Parse headers with semantic sizing
 * Supports # through ###### with optional normalization
 */
function parseHeaders(text: string, normalize: boolean = false): string {
  let result = text;

  // H1: # Header
  result = result.replace(/^# (.+)$/gm, (match, content) => {
    const normalized = normalize ? sentenceCase(content) : content;
    return `<h1>${normalized}</h1>`;
  });

  // H2: ## Header
  result = result.replace(/^## (.+)$/gm, (match, content) => {
    const normalized = normalize ? sentenceCase(content) : content;
    return `<h2>${normalized}</h2>`;
  });

  // H3: ### Header
  result = result.replace(/^### (.+)$/gm, (match, content) => {
    const normalized = normalize ? sentenceCase(content) : content;
    return `<h3>${normalized}</h3>`;
  });

  // H4-H6
  result = result.replace(/^#### (.+)$/gm, '<h4>$1</h4>');
  result = result.replace(/^##### (.+)$/gm, '<h5>$1</h5>');
  result = result.replace(/^###### (.+)$/gm, '<h6>$1</h6>');

  return result;
}

/**
 * Parse inline formatting: bold, italic, strikethrough, inline code
 */
function parseInlineFormatting(text: string): string {
  let result = text;

  // Bold: **text** or __text__
  result = result.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  result = result.replace(/__([^_]+)__/g, '<strong>$1</strong>');

  // Italic: *text* or _text_ (but not inside words or bold)
  result = result.replace(/(?<!\\*)\*(?!\*)([^*]+)\*(?!\*)/g, '<em>$1</em>');
  result = result.replace(/(?<!_)_(?!_)([^_]+)_(?!_)/g, '<em>$1</em>');

  // Strikethrough: ~~text~~
  result = result.replace(/~~([^~]+)~~/g, '<s>$1</s>');

  // Inline code: `code` (mostly redundant since 2026-08-11 — paired backtick
  // spans are stashed opaquely by stashLinksAndCode before this runs — but
  // kept as a harmless backstop for callers that format label fragments).
  result = result.replace(/`([^`]+)`/g, '<code>$1</code>');

  return result;
}

/**
 * Parse lists: ordered and unordered
 * Wraps consecutive list items in <ul> or <ol> with proper indentation
 */
function parseLists(text: string): string {
  let result = text;

  // Unordered lists: - item or * item or • item
  result = result.replace(/^[•\-*]\s+(.+)$/gm, '<li style="margin-bottom:4px;">$1</li>');

  // Ordered lists: 1. item, 2. item, etc.
  result = result.replace(/^\d+\.\s+(.+)$/gm, '<li-ordered style="margin-bottom:4px;">$1</li-ordered>');

  // Wrap consecutive <li> in <ul> with proper left padding
  result = result.replace(/(<li.*?<\/li>\n?)+/gs, (match) => {
    // Remove trailing \n from match to prevent extra <br/> after list
    const cleanMatch = match.replace(/\n$/, '');
    return `<ul style="padding-left:24px;margin:0.5em 0;list-style-type:disc;">${cleanMatch}</ul>`;
  });

  // Wrap consecutive <li-ordered> in <ol> with proper left padding
  result = result.replace(/(<li-ordered.*?<\/li-ordered>\n?)+/gs, (match) => {
    const orderedItems = match.replace(/<li-ordered/g, '<li').replace(/<\/li-ordered>/g, '</li>');
    // Remove trailing \n from match to prevent extra <br/> after list
    const cleanMatch = orderedItems.replace(/\n$/, '');
    return `<ol style="padding-left:24px;margin:0.5em 0;">${cleanMatch}</ol>`;
  });

  return result;
}

/**
 * Group consecutive `> ` lines into a single <blockquote class="chat-quote">
 * (2026-08-17 — email drafts/quoted replies rendered as literal "> " prose).
 * Nested markers (`>> `) are flattened: the quote renders as clean text the
 * user can read and copy — reply-depth is email plumbing, not content. Inner
 * lines keep their raw text and are joined with \n so the later inline and
 * line-break steps format them like ordinary prose. A blank line (or any
 * non-quote line) closes the quote. Runs after the code-block stash, so `>`
 * inside fences is never touched; only line-START markers match, so "5 > 3"
 * mid-sentence is untouched.
 */
export function parseBlockquotes(text: string): string {
  const lines = text.split('\n');
  const out: string[] = [];
  let quote: string[] | null = null;
  const flush = () => {
    if (quote && quote.length > 0) {
      out.push(`<blockquote class="chat-quote">${quote.join('\n')}</blockquote>`);
    }
    quote = null;
  };
  for (const line of lines) {
    const m = line.match(/^\s{0,3}>+\s?(.*)$/);
    if (m) {
      if (!quote) quote = [];
      quote.push(m[1]);
    } else {
      flush();
      out.push(line);
    }
  }
  flush();
  return out.join('\n');
}

/**
 * Parse horizontal rules
 * Supports: --- or *** or ___
 */
function parseHorizontalRules(text: string): string {
  return text.replace(/^(---|\*\*\*|___)$/gm, '<hr/>');
}

/**
 * Format citation references as superscript
 * [1] → <sup>[1]</sup>
 */
function formatCitations(text: string): string {
  return text.replace(/\[(\d+)\]/g, '<sup>[$1]</sup>');
}

/**
 * Convert line breaks to proper HTML elements
 * Uses semantic paragraph tags for paragraph breaks and <br/> for line breaks
 * Prevents double <br/> spacing issues
 */
function convertLineBreaks(text: string): string {
  // Step 1: Replace paragraph breaks (\n\n or more) with paragraph tags
  let result = text;
  result = result.replace(/\n{2,}/g, '</p><p>');

  // Step 2: Wrap content in paragraph tags if not already wrapped
  if (!result.startsWith('<p>') && !result.startsWith('<')) {
    result = '<p>' + result;
  }
  if (!result.endsWith('</p>') && !result.endsWith('>')) {
    result = result + '</p>';
  }

  // Step 3: Convert single newlines to <br/> (within paragraphs)
  result = result.replace(/\n/g, '<br/>');

  return result;
}

/**
 * Sanitize HTML using existing utility
 * Reuses GlobalChatSystem sanitization with allowlist
 */
function sanitizeHtml(html: string): string {
  return sanitizeHtmlInput(html);
}

/**
 * Trim excess whitespace using existing utility
 * Removes stray <br/> tags and normalizes spacing
 */
function trimHtmlWhitespace(html: string): string {
  return trimChatHtmlWhitespace(html);
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Escape HTML special characters
 * Prevents XSS in code blocks and other literal content
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Convert ALL CAPS to Sentence case
 * Used for header normalization
 */
function sentenceCase(text: string): string {
  if (text === text.toUpperCase() && text.length > 1) {
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }
  return text;
}

// ============================================================================
// Exports for Backward Compatibility
// ============================================================================

/**
 * @deprecated Use markdownToHtml() instead
 * Legacy function from agenticWorkflows/helpers.ts
 * Maintained for backward compatibility
 */
export function convertMarkdownToHtml(text: string): string {
  return markdownToHtml(text, {
    normalizeHeaders: true,
    parseTables: false,
    formatCitations: false,
  });
}

/**
 * @deprecated Use markdownToHtml() instead
 * Legacy function from agenticWorkflows/helpers.ts
 * Maintained for backward compatibility
 */
export function formatResearchToHtml(text: string): string {
  return markdownToHtml(text, {
    sourceType: 'perplexity',
    parseTables: true,
    formatCitations: true,
  });
}
